import {
    fetchGensetSetup,
    fetchGensetStatusHydration,
    fetchGensetStatusRefresh,
} from './gensetApi';
import {
    setRefreshState,
    markRefreshTick,
    setLiveFlowState,
    setBatchHydrationState,
} from './gensetSlice';

let refreshTimerId = null;
let runtimeTimerId = null;
const FULL_HYDRATION_BATCH_SIZE = 4;
const MAX_CACHE_EXPIRED_RETRIES = 2;
const isLocalhost = typeof window !== 'undefined'
    && window?.location?.href?.indexOf('localhost') !== -1;
const debugLog = (...args) => {
    if (!isLocalhost) return;
    // eslint-disable-next-line no-console
    console.debug('[gensetFlow]', new Date().toISOString(), ...args);
};

const summarizePayload = (payload) => ({
    user_id: payload?.user_id,
    timeFrom: payload?.timeFrom,
    timeTo: payload?.timeTo,
    includePeriods: payload?.includePeriods,
    includeAddresses: payload?.includeAddresses,
    cacheKey: payload?.cacheKey,
    appendCache: payload?.appendCache,
    expectedImeisCount: Array.isArray(payload?.expectedImeis) ? payload.expectedImeis.length : undefined,
    refreshMode: payload?.refreshMode,
    clientStateVersion: payload?.clientStateVersion,
    imeisCount: Array.isArray(payload?.imeis) ? payload.imeis.length : undefined,
});

const summarizeHydrationResponse = (action) => {
    const data = action?.payload?.data || {};
    return {
        status: data?.status,
        cacheKey: data?.cacheKey,
        stateVersion: data?.stateVersion,
        delta: data?.delta,
        hydrationComplete: data?.hydrationComplete,
        hydratedImeisCount: data?.hydratedImeisCount,
        expectedImeisCount: data?.expectedImeisCount,
        vehiclesCount: Array.isArray(data?.vehicles) ? data.vehicles.length : undefined,
        fleetKeys: data?.fleet ? Object.keys(data.fleet).length : undefined,
    };
};

const isHistoricalPayload = (payload) => {
    if (!payload) return false;
    return Boolean(payload?.timeFrom || payload?.timeTo);
};

const normalizeImei = (value) => String(value || '').trim();
const toFiniteNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const chunkArray = (list = [], size = FULL_HYDRATION_BATCH_SIZE) => {
    if (!Array.isArray(list) || list.length === 0) return [];
    if (!Number.isFinite(size) || size <= 0) return [list];
    const chunks = [];
    for (let index = 0; index < list.length; index += size) {
        chunks.push(list.slice(index, index + size));
    }
    return chunks;
};

const getRunningDurationMinutes = (vehicle) => {
    const running = vehicle?.currentRunning || {};
    const left = Number(running?.Left?.durationMin || running?.left?.durationMin || 0);
    const right = Number(running?.Right?.durationMin || running?.right?.durationMin || 0);
    const single = Number(running?.Single?.durationMin || running?.single?.durationMin || 0);
    return Math.max(left, right, single, 0);
};

const sortImeisByRunningPriority = (vehicles = []) => {
    const normalized = (Array.isArray(vehicles) ? vehicles : [])
        .map((vehicle, index) => ({
            imei: normalizeImei(vehicle?.imei || vehicle?.IMEI || vehicle?.assetId),
            runningNow: Boolean(vehicle?.runningNow),
            durationMin: getRunningDurationMinutes(vehicle),
            vehicleName: String(vehicle?.vehicleName || vehicle?.name || ''),
            index,
        }))
        .filter((item) => Boolean(item.imei));

    normalized.sort((a, b) => {
        if (Number(b.runningNow) !== Number(a.runningNow)) return Number(b.runningNow) - Number(a.runningNow);
        if (b.durationMin !== a.durationMin) return b.durationMin - a.durationMin;
        const byName = a.vehicleName.localeCompare(b.vehicleName);
        if (byName !== 0) return byName;
        return a.index - b.index;
    });
    const seen = new Set();
    return normalized
        .map((item) => item.imei)
        .filter((imei) => {
            if (seen.has(imei)) return false;
            seen.add(imei);
            return true;
        });
};

const stripHydrationControlFields = (payload) => {
    if (!payload) return null;
    const base = { ...payload };
    delete base.cacheKey;
    delete base.includePeriods;
    delete base.includeAddresses;
    delete base.appendCache;
    delete base.expectedImeis;
    // IMEIs from append-cache chunk payload should not become the base request.
    if (payload?.appendCache) {
        delete base.imeis;
    }
    return base;
};

const isCacheExpiredData = (data) => data?.status === 'cache_expired';

const runFastHydration = async (dispatch, baseHydrationPayload) => {
    const fastPayload = {
        ...(baseHydrationPayload || {}),
        includePeriods: false,
        includeAddresses: false,
    };
    debugLog('fast hydration dispatch', summarizePayload(fastPayload));
    const fastAction = await dispatch(fetchGensetStatusHydration(fastPayload));
    debugLog('fast hydration result', summarizeHydrationResponse(fastAction));
    if (!fetchGensetStatusHydration.fulfilled.match(fastAction)) {
        return {
            ok: false,
            cacheExpired: false,
            error: fastAction?.payload?.error || fastAction?.error?.message || 'Fast hydration failed',
        };
    }
    const data = fastAction.payload?.data || {};
    if (isCacheExpiredData(data)) {
        return {
            ok: false,
            cacheExpired: true,
            error: data?.message || 'Fast hydration cache expired',
        };
    }
    return {
        ok: true,
        data,
        cacheKey: data?.cacheKey || null,
        sortedImeis: sortImeisByRunningPriority(data?.vehicles),
    };
};

const runFullHydrationBatches = async (dispatch, {
    baseHydrationPayload,
    cacheKey,
    sortedImeis,
    batchSize = FULL_HYDRATION_BATCH_SIZE,
}) => {
    const expectedImeis = Array.isArray(sortedImeis) ? sortedImeis : [];
    const expectedImeisCount = expectedImeis.length;
    dispatch(setBatchHydrationState({
        active: true,
        hydratedImeisCount: 0,
        expectedImeisCount,
        hydrationComplete: expectedImeisCount === 0,
    }));

    if (!expectedImeisCount) {
        dispatch(setBatchHydrationState({
            active: false,
            hydratedImeisCount: 0,
            expectedImeisCount: 0,
            hydrationComplete: true,
        }));
        return { ok: true, cacheKey };
    }

    const chunks = chunkArray(expectedImeis, batchSize);
    let localHydratedCount = 0;
    let currentCacheKey = cacheKey;

    for (const chunkImeis of chunks) {
        const fullPayload = {
            ...(baseHydrationPayload || {}),
            imeis: chunkImeis,
            includePeriods: true,
            includeAddresses: true,
            appendCache: true,
            expectedImeis,
            cacheKey: currentCacheKey,
        };

        debugLog('full hydration chunk dispatch', summarizePayload(fullPayload));
        const fullAction = await dispatch(fetchGensetStatusHydration(fullPayload));
        debugLog('full hydration chunk result', summarizeHydrationResponse(fullAction));

        if (!fetchGensetStatusHydration.fulfilled.match(fullAction)) {
            dispatch(setBatchHydrationState({ active: false, hydrationComplete: false }));
            return {
                ok: false,
                cacheExpired: false,
                error: fullAction?.payload?.error || fullAction?.error?.message || 'Full hydration failed',
            };
        }

        const data = fullAction.payload?.data || {};
        if (isCacheExpiredData(data)) {
            dispatch(setBatchHydrationState({ active: false, hydrationComplete: false }));
            return {
                ok: false,
                cacheExpired: true,
                error: data?.message || 'Full hydration cache expired',
            };
        }

        if (data?.cacheKey) currentCacheKey = data.cacheKey;
        localHydratedCount += chunkImeis.length;
        const hydratedImeisCount = toFiniteNumber(data?.hydratedImeisCount) ?? localHydratedCount;
        const responseExpectedCount = toFiniteNumber(data?.expectedImeisCount) ?? expectedImeisCount;
        const hydrationComplete = Boolean(data?.hydrationComplete) || hydratedImeisCount >= responseExpectedCount;
        dispatch(setBatchHydrationState({
            active: !hydrationComplete,
            hydratedImeisCount,
            expectedImeisCount: responseExpectedCount,
            hydrationComplete,
        }));
    }

    dispatch(setBatchHydrationState({
        active: false,
        hydratedImeisCount: expectedImeisCount,
        expectedImeisCount,
        hydrationComplete: true,
    }));

    return { ok: true, cacheKey: currentCacheKey };
};

const runHydrationCycle = async (dispatch, {
    hydrationPayload,
    batchSize = FULL_HYDRATION_BATCH_SIZE,
}) => {
    const baseHydrationPayload = stripHydrationControlFields(hydrationPayload || {});
    const fastResult = await runFastHydration(dispatch, baseHydrationPayload);
    if (!fastResult.ok) return fastResult;

    const cacheKey = fastResult?.cacheKey;
    if (!cacheKey) {
        return {
            ok: false,
            cacheExpired: false,
            error: 'Hydration response missing cacheKey',
        };
    }

    const sortedImeis = fastResult?.sortedImeis || [];
    return runFullHydrationBatches(dispatch, {
        baseHydrationPayload,
        cacheKey,
        sortedImeis,
        batchSize,
    });
};

const extractBaseHydrationPayloadForRehydrate = (status) => {
    if (!status) return null;
    const candidate = status?.lastFastHydrationPayload || status?.lastFullHydrationPayload;
    if (!candidate) return null;
    const basePayload = stripHydrationControlFields(candidate);
    if (isHistoricalPayload(basePayload)) return null;
    return basePayload;
};

export const runGensetRefreshTick = () => async (dispatch, getState) => {
    const state = getState();
    const status = state?.genset?.status || {};
    if (status.loading || status.refreshing) {
        debugLog('refreshTick skipped: loading/refreshing', { loading: status.loading, refreshing: status.refreshing });
        return;
    }
    if (status.liveFlowActive || status.batchHydrationActive) {
        debugLog('refreshTick skipped: hydration flow active', {
            liveFlowActive: status.liveFlowActive,
            batchHydrationActive: status.batchHydrationActive,
        });
        return;
    }

    const lastFull = status.lastFullHydrationPayload;
    const lastFast = status.lastFastHydrationPayload;
    const lastPayload = lastFull || lastFast;
    if (isHistoricalPayload(lastPayload)) {
        debugLog('refreshTick skipped: historical payload', summarizePayload(lastPayload));
        return;
    }

    const cacheKey = status.cacheKey;
    if (!cacheKey) {
        debugLog('refreshTick no cacheKey: restarting hydration cycle');
        dispatch(stopGensetRefreshLoop());
        const rehydratePayload = extractBaseHydrationPayloadForRehydrate(status);
        const intervalMs = state?.genset?.refresh?.intervalMs || 180000;
        if (rehydratePayload) {
            await dispatch(startGensetLiveFlow({
                hydrationPayload: rehydratePayload,
                intervalMs,
            }));
        } else {
            debugLog('refreshTick no cacheKey: skipped live flow restart (missing base payload)');
        }
        return;
    }

    debugLog('refreshTick dispatching refresh', { cacheKey, stateVersion: status.stateVersion });
    const action = await dispatch(fetchGensetStatusRefresh({
        cacheKey,
        refreshMode: 'delta',
        clientStateVersion: status.stateVersion,
    }));
    dispatch(markRefreshTick());

    if (fetchGensetStatusRefresh.fulfilled.match(action)) {
        const data = action.payload?.data;
        debugLog('refreshTick fulfilled', summarizeHydrationResponse(action));
        if (data?.status === 'cache_expired') {
            debugLog('refreshTick cache_expired: restarting hydration cycle');
            dispatch(stopGensetRefreshLoop());
            const nextState = getState();
            const nextStatus = nextState?.genset?.status || {};
            const rehydratePayload = extractBaseHydrationPayloadForRehydrate(nextStatus);
            const intervalMs = nextState?.genset?.refresh?.intervalMs || 180000;
            if (rehydratePayload) {
                await dispatch(startGensetLiveFlow({
                    hydrationPayload: rehydratePayload,
                    intervalMs,
                }));
            } else {
                debugLog('refreshTick cache_expired: skipped live flow restart (missing base payload)');
            }
        }
    } else {
        debugLog('refreshTick rejected', { error: action?.payload?.error || action?.error?.message });
    }
};

export const startGensetRefreshLoop = ({ intervalMs = 180000 } = {}) => (dispatch) => {
    if (refreshTimerId) {
        clearInterval(refreshTimerId);
        refreshTimerId = null;
    }
    dispatch(setRefreshState({ active: true, intervalMs }));
    debugLog('startRefreshLoop', { intervalMs });
    refreshTimerId = setInterval(() => {
        dispatch(runGensetRefreshTick());
    }, intervalMs);
};

export const stopGensetRefreshLoop = () => (dispatch) => {
    if (refreshTimerId) {
        clearInterval(refreshTimerId);
        refreshTimerId = null;
    }
    dispatch(setRefreshState({ active: false }));
    debugLog('stopRefreshLoop');
};

export const startGensetRuntimeLoop = ({ payload, intervalMs = 3600000 } = {}) => () => {
    if (runtimeTimerId) {
        clearInterval(runtimeTimerId);
        runtimeTimerId = null;
    }
    debugLog('startRuntimeLoop skipped: runtime API disabled', { intervalMs, payload: summarizePayload(payload) });
};

export const stopGensetRuntimeLoop = () => (_dispatch) => {
    if (runtimeTimerId) {
        clearInterval(runtimeTimerId);
        runtimeTimerId = null;
    }
    debugLog('stopRuntimeLoop');
};

export const startGensetLiveFlow = ({
    setupPayload,
    hydrationPayload,
    intervalMs = 180000,
    batchSize = FULL_HYDRATION_BATCH_SIZE,
    maxRetries = MAX_CACHE_EXPIRED_RETRIES,
} = {}) => async (dispatch, getState) => {
    const status = getState()?.genset?.status || {};
    if (status.liveFlowActive) {
        debugLog('startLiveFlow skipped: already active');
        return;
    }

    dispatch(setLiveFlowState({ active: true }));
    dispatch(stopGensetRefreshLoop());
    debugLog('startLiveFlow', {
        setup: summarizePayload(setupPayload),
        hydration: summarizePayload(hydrationPayload),
        intervalMs,
        batchSize,
        maxRetries,
    });

    try {
        if (setupPayload) {
            debugLog('setup dispatch', summarizePayload(setupPayload));
            dispatch(fetchGensetSetup(setupPayload));
        }

        let attempt = 0;
        while (attempt <= maxRetries) {
            const cycleResult = await runHydrationCycle(dispatch, {
                hydrationPayload,
                batchSize,
            });
            if (cycleResult?.ok) {
                debugLog('hydration cycle complete: starting refresh loop', { intervalMs });
                dispatch(startGensetRefreshLoop({ intervalMs }));
                return;
            }

            const error = cycleResult?.error || 'Hydration cycle failed';
            if (!cycleResult?.cacheExpired) {
                debugLog('hydration cycle failed', { error });
                return;
            }

            attempt += 1;
            if (attempt > maxRetries) {
                debugLog('hydration cycle failed after cache_expired retries', { retries: maxRetries, error });
                return;
            }
            debugLog('hydration cycle cache_expired: retrying from fast hydration', { retry: attempt, maxRetries });
        }
    } finally {
        dispatch(setBatchHydrationState({ active: false }));
        dispatch(setLiveFlowState({ active: false }));
    }
};
